import type { IProductBundleOption } from "@rogue/universal"
import { BundleUIOption } from "../RuleTypes"
import { IRogue, Product, BuyRequest } from "@/js/services/RogueTypes"
import { HYBRID_DIVIDER_SHELF, HYBRID_FIXED_70_WIDTH_CROSSMEMBER_TITLES, HYBRID_TOP_SHELF_MAX_DIVIDERS_BELOW } from "@/js/pages/product/rackbuilder/products/rig/hybrid/hybridConstants"
import { getHybridHeight, getHybridWidth } from "@/js/pages/product/rackbuilder/products/rig/hybrid/hybridUtils"
import { getShelfSelections, normalizeShelfName } from "@/js/pages/product/rackbuilder/utils/selection-utils"
import { MAX_SHELVES } from "../../custom/useShelfVisibility"
import { getOptionsForRackBundle } from "./rack-rules"

// Hybrid's units are plain accessories to one shared central rack, so this reuses the standard rack rules as-is; called for both the rack section and each unit, whichever bundle's own Width applies.
export function getOptionsForHybridRig(rogue: IRogue, rack: Product, bundleOption: IProductBundleOption, options: BundleUIOption[], bundleBuyRequest: BuyRequest) {
  const width = getHybridWidth(rogue, bundleBuyRequest)

  // At 70" both lines render the 70" crossmember mesh, which exists in Satin Black only and never as a nameplate. Filtered rather than forced in the builders so the rendered finish always equals the selection.
  if (width === '70"' && HYBRID_FIXED_70_WIDTH_CROSSMEMBER_TITLES.includes(bundleOption.title)) {
    return options.filter(opt => {
      if (!opt.value) return true

      const product = rogue.getProductById(opt.value)

      return product?.attributes?.beam_color === 'Satin Black' && !String(product.name ?? '').includes('Nameplate')
    })
  }

  // Shelves come in one width each, so only the ones spanning this unit offer a valid order line - same rule cave-rules applies to its own shelf rows. Matched on product name rather than label, which bundle-option.vue rewrites to strip the shared prefix.
  const shelfWidthMatches = (opt: BundleUIOption) => !opt.value || String(rogue.getProductById(opt.value)?.name ?? '').includes(width as string)
  const matchingOptions = width && String(bundleOption.title ?? '').toLowerCase().includes('shelf')
    ? options.filter(shelfWidthMatches)
    : options

  // MARK: Top shelf row
  // Past the rig's divider allowance the top row is retracted outright rather than filtered - returning false empties its option list and bundle-option's availableOptions watcher zeroes whatever was selected there. Evaluated on every render like the Monster Mass Storage shelf rules, which is what makes the cap hold whichever order the shopper builds in: fill all five rows and then pick a divider lower down, and the row is withdrawn on the next render instead of standing as an over-tall rig.
  if (getHybridShelfKey(bundleOption.title) === HYBRID_TOP_SHELF_KEY) {
    if (!hasRoomForTopShelf(rogue, bundleBuyRequest)) return false

    // The row stands, but a divider is never offered on it at either height - the top tray is the one with no clearance above it.
    return getOptionsForRackBundle(rogue, rack, bundleOption, withoutDividerOption(rogue, matchingOptions), bundleBuyRequest)
  }

  return getOptionsForRackBundle(rogue, rack, bundleOption, matchingOptions, bundleBuyRequest)
}

const HYBRID_TOP_SHELF_KEY = `shelf${MAX_SHELVES}`

// 'Shelf 4:' -> 'shelf4', the key getShelfSelections files that row's selection under. Returns undefined for every non-shelf option title.
function getHybridShelfKey(bundleOptionTitle: string | undefined): string | undefined {
  const match = String(bundleOptionTitle ?? '').match(/shelf\s*(\d+)/i)

  return match ? `shelf${match[1]}` : undefined
}

// The top row is excluded deliberately: it is the row these rules retract, and counting its own selection would let a divider sitting there retract the row out from under itself and then reinstate it on the next render.
function getHybridDividerCountBelowTop(rogue: IRogue, bundleBuyRequest: BuyRequest): number {
  return Object.entries(getShelfSelections(rogue, bundleBuyRequest))
    .filter(([key, shelf]) => shelf === HYBRID_DIVIDER_SHELF && key !== HYBRID_TOP_SHELF_KEY)
    .length
}

// Whether this rig still has the headroom for its fifth shelf - how many dividers that tolerates is the one thing the two rack heights differ on.
function hasRoomForTopShelf(rogue: IRogue, bundleBuyRequest: BuyRequest): boolean {
  const height = getHybridHeight(rogue, bundleBuyRequest) ?? ''
  const allowed = HYBRID_TOP_SHELF_MAX_DIVIDERS_BELOW[height] ?? 0

  return getHybridDividerCountBelowTop(rogue, bundleBuyRequest) <= allowed
}

function withoutDividerOption(rogue: IRogue, options: BundleUIOption[]): BundleUIOption[] {
  return options.filter(opt => !opt.value || normalizeShelfName(rogue.getProductById(opt.value)?.name) !== HYBRID_DIVIDER_SHELF)
}

// Gates the "Add another shelf" button. Only the jump to the top row is ever blocked, and only on a rig past its divider allowance - revealing a row the rule above would immediately retract would leave the button looking broken. Every earlier row is always reachable, which is what keeps a four-divider rig buildable.
export function canAddHybridShelf(rogue: IRogue, bundleBuyRequest: BuyRequest | undefined, nextShelfNumber: number): boolean {
  if (!bundleBuyRequest || nextShelfNumber < MAX_SHELVES) return true

  return hasRoomForTopShelf(rogue, bundleBuyRequest)
}
